import React, { useState, useRef, useMemo } from "react";
import { motion, useInView, AnimatePresence } from "framer-motion";
import { useForm } from "react-hook-form";
import {
  Mail,
  Linkedin,
  Camera,
  Send,
  MapPin,
  Phone,
  CheckCircle2,
  AlertCircle,
  Sparkles,
  MessageSquare,
  User,
  AtSign
} from "lucide-react";

const ContactSection = ({ nightMode = false }) => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-100px" });
  const [submitStatus, setSubmitStatus] = useState(null);
  const [focusedField, setFocusedField] = useState(null);
  const [mousePosition, setMousePosition] = useState({ x: 50, y: 50 });
  const [isMobile, setIsMobile] = useState(false);
  const throttleTimeout = useRef(null);

  // Check if mobile
  React.useEffect(() => {
    const checkMobile = () => setIsMobile(window.innerWidth <= 768);
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  // Track mouse position for interactive effects - throttled for performance
  const handleMouseMove = (e) => {
    if (isMobile) return;

    // Throttle to 60fps max
    if (throttleTimeout.current) return;

    throttleTimeout.current = setTimeout(() => {
      throttleTimeout.current = null;
    }, 16); // ~60fps

    const rect = ref.current?.getBoundingClientRect();
    if (rect) {
      setMousePosition({
        x: ((e.clientX - rect.left) / rect.width) * 100,
        y: ((e.clientY - rect.top) / rect.height) * 100,
      });
    }
  };

  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
    reset,
  } = useForm();

  const onSubmit = async (data) => {
    try {
      const response = await fetch("https://formspree.io/f/xpwozapo", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(data),
      });

      if (response.ok) {
        setSubmitStatus("success");
        reset();
        setTimeout(() => setSubmitStatus(null), 4000);
      } else {
        setSubmitStatus("error");
        setTimeout(() => setSubmitStatus(null), 4000);
      }
    } catch (error) {
      setSubmitStatus("error");
      setTimeout(() => setSubmitStatus(null), 4000);
    }
  };

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.15,
        delayChildren: 0.2,
      },
    },
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 30 },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.6,
        ease: [0.22, 1, 0.36, 1],
      },
    },
  };

  const formFieldVariants = {
    hidden: { opacity: 0, x: -20 },
    visible: {
      opacity: 1,
      x: 0,
      transition: {
        duration: 0.5,
        ease: [0.22, 1, 0.36, 1],
      },
    },
  };

  const contactMethods = [
    {
      icon: Mail,
      label: "Email",
      value: "surmayeeu@gmail.com",
      href: "mailto:surmayeeu@gmail.com",
      color: "#FFB6D9"
    },
    {
      icon: MapPin,
      label: "Location",
      value: "Paris, France",
      color: "#B8A4D8"
    },
    {
      icon: Phone,
      label: "Response Time",
      value: "Within 24 hours",
      color: "#A8D4FF"
    }
  ];


  return (
    <section
      id="contact"
      className={`contact-section-luxe ${nightMode ? 'contact-section-luxe--dark' : ''}`}
      ref={ref}
      onMouseMove={handleMouseMove}
    >
      {/* Interactive Animated Background */}
      <div className="contact-interactive-bg">
        {/* Animated Grid */}
        <div className="contact-animated-grid">
          <svg className="contact-grid-svg" xmlns="http://www.w3.org/2000/svg">
            <defs>
              <pattern id="contact-grid-pattern" x="0" y="0" width="60" height="60" patternUnits="userSpaceOnUse">
                <path d="M 60 0 L 0 0 0 60" fill="none" stroke="rgba(255, 182, 217, 0.15)" strokeWidth="1"/>
              </pattern>
            </defs>
            <rect width="100%" height="100%" fill="url(#contact-grid-pattern)" className="contact-grid-rect"/>
          </svg>
        </div>

        {/* Interactive Cursor Glow - disabled on mobile */}
        {!isMobile && (
          <motion.div
            className="contact-cursor-glow"
            animate={{
              x: `${mousePosition.x}%`,
              y: `${mousePosition.y}%`,
            }}
            transition={{
              type: "tween",
              duration: 0.4,
              ease: "easeOut",
            }}
          />
        )}

        {/* Floating Interactive Particles - optimized count */}
        {useMemo(() => [...Array(isMobile ? 6 : 10)].map((_, i) => {
          // Generate random positions for more organic distribution
          const randomX = Math.random() * 100;
          const randomY = Math.random() * 100;
          const randomMoveX = (Math.random() - 0.5) * 40;
          const randomMoveY = (Math.random() - 0.5) * 40;
          const randomDuration = 4 + Math.random() * 3;
          const randomDelay = Math.random() * 2;

          return (
            <motion.div
              key={i}
              className="contact-floating-particle"
              style={{
                left: `${randomX}%`,
                top: `${randomY}%`,
              }}
              animate={{
                y: [0, randomMoveY, 0],
                x: [0, randomMoveX, 0],
                scale: [1, 1.15, 1],
                opacity: [0.3, 0.6, 0.3],
              }}
              transition={{
                duration: randomDuration,
                repeat: Infinity,
                delay: randomDelay,
                ease: "linear",
              }}
            />
          );
        }), [isMobile])}

        {/* Flowing gradient beams */}
        <div className="contact-gradient-beam contact-beam-1"></div>
        <div className="contact-gradient-beam contact-beam-2"></div>

        {/* Subtle shimmer overlay */}
        <div className="contact-shimmer-overlay"></div>

        {/* Animated gradient orbs that follow cursor subtly - disabled on mobile */}
        {!isMobile && (
          <motion.div
            className="contact-interactive-orb contact-orb-interactive-1"
            animate={{
              x: mousePosition.x * 0.3,
              y: mousePosition.y * 0.3,
            }}
            transition={{
              type: "tween",
              duration: 0.6,
              ease: "easeOut",
            }}
          />
        )}
      </div>

      <div className="contact-container-luxe">
        <motion.div
          className="contact-content-luxe"
          variants={containerVariants}
          initial="hidden"
          animate={isInView ? "visible" : "hidden"}
        >
          {/* Header */}
          <motion.div className="contact-header-luxe" variants={itemVariants}>
            <motion.div
              className="contact-label-luxe"
              initial={{ opacity: 0, y: 20 }}
              animate={isInView ? { opacity: 1, y: 0 } : { opacity: 0, y: 20 }}
              transition={{ duration: 0.6 }}
            >
              <Sparkles size={14} />
              <span>GET IN TOUCH</span>
            </motion.div>
            <h2 className="contact-title-luxe">
              <motion.span
                initial={{ opacity: 0, y: 30 }}
                animate={isInView ? { opacity: 1, y: 0 } : { opacity: 0, y: 30 }}
                transition={{ duration: 0.8, delay: 0.2 }}
              >
                Let's create
              </motion.span>
              <br />
              <motion.span
                initial={{ opacity: 0, y: 30 }}
                animate={isInView ? { opacity: 1, y: 0 } : { opacity: 0, y: 30 }}
                transition={{ duration: 0.8, delay: 0.3 }}
                className="contact-title-gradient"
              >
                something amazing
              </motion.span>
            </h2>
            <motion.p
              className="contact-description-luxe"
              initial={{ opacity: 0, y: 20 }}
              animate={isInView ? { opacity: 1, y: 0 } : { opacity: 0, y: 20 }}
              transition={{ duration: 0.6, delay: 0.4 }}
            >
              I'm always interested in hearing about new opportunities in luxury brand
              strategy, merchandising, and fashion innovation. Let's discuss how we can
              work together.
            </motion.p>
          </motion.div>

          {/* Form Section - Centered */}
          <motion.div
            className="contact-form-centered"
            variants={itemVariants}
          >
            <form onSubmit={handleSubmit(onSubmit)} className="contact-form-luxe">
              {/* Name & Email Row */}
              <div className="form-row">
                <motion.div
                  className="form-group-luxe"
                  variants={formFieldVariants}
                >
                  <label htmlFor="name" className="form-label-luxe">
                    <User size={16} />
                    <span>Your Name</span>
                  </label>
                  <div className={`form-input-wrapper ${focusedField === 'name' ? 'focused' : ''} ${errors.name ? 'error' : ''}`}>
                    <input
                      type="text"
                      id="name"
                      className="form-input-luxe"
                      placeholder="John Doe"
                      {...register("name", {
                        required: "Name is required",
                        minLength: { value: 2, message: "Name is too short" }
                      })}
                      onFocus={() => setFocusedField('name')}
                      onBlur={() => setFocusedField(null)}
                    />
                    {focusedField === 'name' && (
                      <motion.div
                        className="input-focus-line"
                        layoutId="focusLine"
                        transition={{ duration: 0.3 }}
                      />
                    )}
                  </div>
                  <AnimatePresence>
                    {errors.name && (
                      <motion.span
                        className="form-error"
                        initial={{ opacity: 0, y: -10 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, y: -10 }}
                      >
                        <AlertCircle size={14} />
                        {errors.name.message}
                      </motion.span>
                    )}
                  </AnimatePresence>
                </motion.div>

                <motion.div
                  className="form-group-luxe"
                  variants={formFieldVariants}
                >
                  <label htmlFor="email" className="form-label-luxe">
                    <AtSign size={16} />
                    <span>Email Address</span>
                  </label>
                  <div className={`form-input-wrapper ${focusedField === 'email' ? 'focused' : ''} ${errors.email ? 'error' : ''}`}>
                    <input
                      type="email"
                      id="email"
                      className="form-input-luxe"
                      placeholder="john@example.com"
                      {...register("email", {
                        required: "Email is required",
                        pattern: {
                          value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                          message: "Invalid email address"
                        }
                      })}
                      onFocus={() => setFocusedField('email')}
                      onBlur={() => setFocusedField(null)}
                    />
                    {focusedField === 'email' && (
                      <motion.div
                        className="input-focus-line"
                        layoutId="focusLine"
                        transition={{ duration: 0.3 }}
                      />
                    )}
                  </div>
                  <AnimatePresence>
                    {errors.email && (
                      <motion.span
                        className="form-error"
                        initial={{ opacity: 0, y: -10 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, y: -10 }}
                      >
                        <AlertCircle size={14} />
                        {errors.email.message}
                      </motion.span>
                    )}
                  </AnimatePresence>
                </motion.div>
              </div>

              {/* Subject */}
              <motion.div
                className="form-group-luxe"
                variants={formFieldVariants}
              >
                <label htmlFor="subject" className="form-label-luxe">
                  <MessageSquare size={16} />
                  <span>Subject</span>
                </label>
                <div className={`form-input-wrapper ${focusedField === 'subject' ? 'focused' : ''} ${errors.subject ? 'error' : ''}`}>
                  <input
                    type="text"
                    id="subject"
                    className="form-input-luxe"
                    placeholder="What would you like to discuss?"
                    {...register("subject", {
                      required: "Subject is required",
                      minLength: { value: 5, message: "Subject is too short" }
                    })}
                    onFocus={() => setFocusedField('subject')}
                    onBlur={() => setFocusedField(null)}
                  />
                  {focusedField === 'subject' && (
                    <motion.div
                      className="input-focus-line"
                      layoutId="focusLine"
                      transition={{ duration: 0.3 }}
                    />
                  )}
                </div>
                <AnimatePresence>
                  {errors.subject && (
                    <motion.span
                      className="form-error"
                      initial={{ opacity: 0, y: -10 }}
                      animate={{ opacity: 1, y: 0 }}
                      exit={{ opacity: 0, y: -10 }}
                    >
                      <AlertCircle size={14} />
                      {errors.subject.message}
                    </motion.span>
                  )}
                </AnimatePresence>
              </motion.div>

              {/* Message */}
              <motion.div
                className="form-group-luxe"
                variants={formFieldVariants}
              >
                <label htmlFor="message" className="form-label-luxe">
                  <Mail size={16} />
                  <span>Message</span>
                </label>
                <div className={`form-input-wrapper ${focusedField === 'message' ? 'focused' : ''} ${errors.message ? 'error' : ''}`}>
                  <textarea
                    id="message"
                    className="form-textarea-luxe"
                    rows="5"
                    placeholder="Tell me about your project, opportunity, or just say hello..."
                    {...register("message", {
                      required: "Message is required",
                      minLength: { value: 10, message: "Message is too short" }
                    })}
                    onFocus={() => setFocusedField('message')}
                    onBlur={() => setFocusedField(null)}
                  />
                  {focusedField === 'message' && (
                    <motion.div
                      className="input-focus-line"
                      layoutId="focusLine"
                      transition={{ duration: 0.3 }}
                    />
                  )}
                </div>
                <AnimatePresence>
                  {errors.message && (
                    <motion.span
                      className="form-error"
                      initial={{ opacity: 0, y: -10 }}
                      animate={{ opacity: 1, y: 0 }}
                      exit={{ opacity: 0, y: -10 }}
                    >
                      <AlertCircle size={14} />
                      {errors.message.message}
                    </motion.span>
                  )}
                </AnimatePresence>
              </motion.div>

              {/* Submit Button */}
              <motion.button
                type="submit"
                className="form-submit-luxe"
                disabled={isSubmitting}
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
              >
                <motion.div
                  className="submit-content"
                  animate={isSubmitting ? { opacity: 0 } : { opacity: 1 }}
                >
                  <span>Send Message</span>
                  <Send size={18} />
                </motion.div>

                {isSubmitting && (
                  <motion.div
                    className="submit-loader"
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                  >
                    <div className="loader-spinner" />
                  </motion.div>
                )}
              </motion.button>

              {/* Status Messages */}
              <AnimatePresence>
                {submitStatus && (
                  <motion.div
                    className={`form-status-luxe ${submitStatus}`}
                    initial={{ opacity: 0, y: -20, scale: 0.9 }}
                    animate={{ opacity: 1, y: 0, scale: 1 }}
                    exit={{ opacity: 0, y: -20, scale: 0.9 }}
                  >
                    {submitStatus === 'success' ? (
                      <>
                        <CheckCircle2 size={20} />
                        <span>Message sent successfully! I'll get back to you soon.</span>
                      </>
                    ) : (
                      <>
                        <AlertCircle size={20} />
                        <span>Oops! Something went wrong. Please try again.</span>
                      </>
                    )}
                  </motion.div>
                )}
              </AnimatePresence>
            </form>
          </motion.div>
        </motion.div>

      </div>
    </section>
  );
};

export default ContactSection;